import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const ROLE_ALLOWED_PATHS = {
  initial:  ['/account-matching', '/profile', '/help'],
  operator: ['/generation-scheduling', '/schedule', '/profile', '/help'],
  full:     null  // no restriction
}

const STICKY_HEADER_OFFSET = 96

const ROLE_DEFAULT_PATH = {
  initial:  '/profile',
  operator: '/generation-scheduling',
  full:     '/'
}

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/auth/Login.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('@/views/auth/Register.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/password-reset',
    name: 'PasswordReset',
    component: () => import('@/views/auth/PasswordReset.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/password-reset-confirm',
    name: 'PasswordResetConfirm',
    component: () => import('@/views/auth/PasswordResetConfirm.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/verify-email',
    name: 'VerifyEmail',
    component: () => import('@/views/auth/VerifyEmail.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/',
    component: () => import('@/components/layout/AppLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'Dashboard',
        component: () => import('@/views/Dashboard.vue')
      },
      {
        path: 'account-matching',
        name: 'AccountMatching',
        component: () => import('@/views/AccountMatching.vue')
      },
      {
        path: 'customers',
        name: 'Customers',
        component: () => import('@/views/Customers.vue')
      },
      {
        path: 'customers/:id',
        name: 'CustomerDetail',
        component: () => import('@/views/CustomerDetail.vue')
      },
      {
        path: 'consumption-points',
        name: 'ConsumptionPoints',
        component: () => import('@/views/ConsumptionPoints.vue')
      },
      {
        path: 'consumption-points/:id',
        name: 'ConsumptionPointDetail',
        component: () => import('@/views/ConsumptionPointDetail.vue')
      },
      {
        path: 'generation-scheduling',
        name: 'GenerationScheduling',
        component: () => import('@/views/GenerationScheduling.vue')
      },
      {
        path: 'schedule/:ppeId',
        name: 'ScheduleManagement',
        component: () => import('@/views/ScheduleManagement.vue')
      },
      {
        path: 'invoices',
        name: 'Invoices',
        component: () => import('@/views/Invoices.vue')
      },
      {
        path: 'invoices/:id',
        name: 'InvoiceDetail',
        component: () => import('@/views/InvoiceDetail.vue')
      },
      {
        path: 'contracts',
        name: 'Contracts',
        component: () => import('@/views/Contracts.vue')
      },
      {
        path: 'contracts/:id',
        name: 'ContractDetail',
        component: () => import('@/views/ContractDetail.vue')
      },
      {
        path: 'issues',
        name: 'Issues',
        component: () => import('@/views/Issues.vue')
      },
      {
        path: 'issues/new',
        name: 'NewIssue',
        component: () => import('@/views/NewIssue.vue')
      },
      {
        path: 'meter-reading/new',
        name: 'NewMeterReading',
        component: () => import('@/views/NewMeterReading.vue')
      },
      {
        path: 'issues/:id',
        name: 'IssueDetail',
        component: () => import('@/views/IssueDetail.vue')
      },
      {
        path: 'profile',
        name: 'Profile',
        component: () => import('@/views/Profile.vue')
      },
      {
        path: 'operator-management',
        name: 'OperatorManagement',
        component: () => import('@/views/OperatorManagement.vue'),
        meta: { requiresRole: 'full' }
      },
      {
        path: 'impersonate',
        name: 'ImpersonatePanel',
        component: () => import('@/views/ImpersonatePanel.vue'),
        meta: { requiresRole: 'full', requiresEpAdmin: true }
      },
      {
        path: 'help',
        name: 'Help',
        component: () => import('@/views/Help.vue')
      },
      {
        path: 'help/:section',
        name: 'HelpSection',
        component: () => import('@/views/Help.vue')
      }
    ]
  },
  {
    path: '/payment-result',
    name: 'PaymentResult',
    component: () => import('@/views/PaymentResult.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/pay',
    name: 'PayInvoice',
    component: () => import('@/views/PayInvoice.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/health',
    name: 'HealthCheck',
    component: () => import('@/views/health/HealthCheck.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    redirect: '/'
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition
    if (to.hash && !to.path.startsWith('/help')) {
      return {
        el: to.hash,
        top: STICKY_HEADER_OFFSET,
        behavior: 'smooth',
      }
    }
    if (to.path !== from.path) {
      return { top: 0 }
    }
    return false
  }
})

function defaultPathForRole(role) {
  return ROLE_DEFAULT_PATH[role] || '/'
}

function isPathAllowedForRole(path, role) {
  const allowed = ROLE_ALLOWED_PATHS[role]
  if (!allowed) return true
  return allowed.some(
    prefix => path === prefix || path.startsWith(prefix + '/')
  )
}

/**
 * Validate a user-controlled `redirect` value before navigating to it after
 * login, falling back to the role's default route (SEZ-5044-R03).
 */
export function resolveLoginRedirect(
  rawRedirect,
  effectiveRole,
  { isEpAdmin = false } = {}
) {
  // Normalised here, not only in the guard: this is the function that decides
  // whether a user-supplied address is allowed, and an unknown role must not
  // mean "everything is allowed".
  const role = effectiveRole || 'initial'
  const fallback = defaultPathForRole(role)

  if (typeof rawRedirect !== 'string' || !rawRedirect) return fallback
  // Must be a path inside this application: one leading slash, no
  // protocol-relative form, no scheme, no backslash variants of either.
  if (rawRedirect.includes('\\')) return fallback
  if (!rawRedirect.startsWith('/') || rawRedirect.startsWith('//')) return fallback
  if (rawRedirect.includes('://')) return fallback

  const resolved = router.resolve(rawRedirect)
  if (!resolved.matched.length || resolved.name === 'NotFound') return fallback
  // Routes that do not require a session sit outside the role gate, exactly
  // as in the guard below.
  const isRoleGated = resolved.meta.requiresAuth !== false
  if (isRoleGated) {
    if (!isPathAllowedForRole(resolved.path, role)) return fallback
    // The same gates the guard applies; without them the user is sent to a
    // route it is about to bounce them off again.
    if (resolved.meta.requiresRole && resolved.meta.requiresRole !== role) {
      return fallback
    }
    if (resolved.meta.requiresEpAdmin && !isEpAdmin) return fallback
  }

  return resolved.fullPath
}

// Navigation guard
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // Only a destination that needs a session brings one up. A public route —
  // the payment link from an email above all — must render without waiting
  // on a refresh round trip and without a single logged-in request
  // (SEZ-5044-R08).
  if (to.meta.requiresAuth !== false) {
    await authStore.startSession()
  }

  if (to.meta.requiresAuth && !authStore.hasUsableSession()) {
    return next({
      path: '/login',
      query: { redirect: to.fullPath }
    })
  }

  if (to.path === '/login' && authStore.hasUsableSession()) {
    const role = authStore.userRole || 'initial'
    return next(defaultPathForRole(role))
  }

  if (authStore.hasUsableSession() && to.meta.requiresAuth !== false) {
    // Role-gated routes (requiresRole / requiresEpAdmin) must decide against
    // server-confirmed values. On a deep-link or refresh the role/ep-admin
    // state may not be hydrated yet, so await a first fetch before deciding —
    // otherwise an EP admin is wrongly bounced from /impersonate (SEZ-4444-R12).
    if (
      (to.meta.requiresEpAdmin || to.meta.requiresRole) &&
      !authStore.roleFetched
    ) {
      await authStore.fetchUserRole()
    }

    const role = authStore.userRole || 'initial'

    if (to.meta.requiresEpAdmin && !authStore.isEpAdmin) {
      return next(defaultPathForRole(role))
    }

    if (to.meta.requiresEpAdmin && authStore.isEpAdmin) {
      return next()
    }

    if (to.meta.requiresRole && to.meta.requiresRole !== role) {
      return next(defaultPathForRole(role))
    }

    if (!isPathAllowedForRole(to.path, role)) {
      return next(defaultPathForRole(role))
    }
  }

  next()
})

export default router
